import React, { useCallback, useContext, useMemo, useState } from "react";

import {
   copyV3ListWithParentForm,
   deleteV3List,
   getListStruct,
} from "../../../../services/restAPI/ListsV3";
import { FormBuilderContext } from "../../FormBuilder/FormBuilderContext";
import { useListBuilder } from "../../../../context/ListBuilderContext/ListBuilderContext";
import { Message, Modal } from "../../../BP2Components";
import { listElement } from "../ListBuildertypes";
import { useIntl } from "react-intl";

import FormV3Controller from "../../FormsV3/FormV3Controller";
import IntlMessages from "../../../../util/IntlMessages";
import ListBuilderActions from "./ListBuilderActions";
import ListBuilderSearch from "./ListBuilderSearch";
import CreateOrEditListModal from "./ListBuilderModals/CreateNewListModal";
import { normalizeAction } from "../ListBuilderScripts";

interface ListBuilderTableProps {
   data: {
      data: listElement[];
      page: number;
      size: number;
      total: number;
   };
}

const ListBuilderTable: React.FC<ListBuilderTableProps> = ({ data }) => {
   const [activeSearchColumn, setActiveSearchColumn] = useState<string | null>(null);
   const [isFormModalOpen, setIsFormModalOpen] = useState(false);
   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
   const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
   const [selectedRow, setSelectedRow] = useState<listElement>();
   const [formStatus, setFormStatus] = useState("");
   const [loadingRowId, setLoadingRowId] = useState<string | null>(null);

   const { setEditedForm } = useContext(FormBuilderContext);
   const { setCurrentTab, refreshList, removeFromMainList } = useListBuilder();

   const intl = useIntl();

   const columns = useMemo(() => {
      if (!Array.isArray(data?.data) || data?.data?.length === 0) return [];

      const baseColumns = Object.keys(data.data[0]).map((key) => ({
         name: key,
         key,
      }));

      return [...baseColumns, { name: "actions", key: "actions" }];
   }, [data]);

   const handleShowParentForm = useCallback((formId: string) => {
      setSelectedFormId(formId);
      setIsFormModalOpen(true);
   }, []);

   const handleShowEditForm = useCallback(async (row: listElement) => {
      setLoadingRowId(row.listId);
      try {
         const { data: struct } = await getListStruct(row.listId);
         setSelectedRow({
            ...row,
            pageSize: struct.pageSize,
            addItemFormId: struct.addItemFormId,
            actions: (struct.actions ?? []).map(normalizeAction),
         });
         setIsEditModalOpen(true);
      } catch (err) {
         console.error(err);
         Message.error(intl.formatMessage({ id: "global.error" }));
      } finally {
         setLoadingRowId(null);
      }
   }, []);

   const handleCloseModal = useCallback(() => {
      setIsFormModalOpen(false);
      setSelectedFormId(null);
      setFormStatus("");
   }, []);

   const handleOpenParentFormEdit = useCallback((row: listElement) => {
      setEditedForm(row?.presenterFormId);
      setCurrentTab(2);
   }, []);

   const handleCopyList = useCallback(async (listId, copyParentForm) => {
      try {
         await copyV3ListWithParentForm(listId, copyParentForm);
         Message.success(intl.formatMessage({ id: "global.success" }));
         await refreshList();
      } catch (err) {
         console.error(err);
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   }, []);

   const handleListDelete = useCallback(async (listId) => {
      try {
         await deleteV3List(listId)?.then(() => removeFromMainList(listId));
         Message.success(intl.formatMessage({ id: "global.success" }));
         await refreshList();
      } catch (err) {
         console.error(err);
         Message.error(intl.formatMessage({ id: "global.error" }));
      }
   }, []);

   return (
      <div className="lb-table-wrapper">
         <table className="lb-table">
            <thead>
               <tr>
                  {columns.map((col) => (
                     <th
                        key={col.key}
                        style={{
                           whiteSpace: "normal",
                           wordBreak: "break-word",
                           verticalAlign: "top",
                        }}
                     >
                        <div
                           style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                           }}
                        >
                           <IntlMessages id={`listBuilder-${col.key}`} />
                           {col?.key !== "actions" && (
                              <ListBuilderSearch
                                 col={col}
                                 activeSearchColumn={activeSearchColumn}
                                 setActiveSearchColumn={setActiveSearchColumn}
                              />
                           )}
                        </div>
                     </th>
                  ))}
               </tr>
            </thead>

            <tbody>
               {Array.isArray(data?.data) &&
                  data.data.map((row) => (
                     <tr key={row.listId}>
                        {columns.map((col) => (
                           <td
                              key={col.key}
                              style={{
                                 maxWidth: "180px",
                                 whiteSpace: "normal",
                                 wordBreak: "break-word",
                                 verticalAlign: "top",
                              }}
                           >
                              {col.key === "presenterFormId" ? (
                                 <div
                                    style={{
                                       display: "flex",
                                       flexDirection: "row",
                                       alignItems: "center",
                                    }}
                                 >
                                    <span>{(row as any)[col.key] || ""}</span>
                                 </div>
                              ) : col.key === "actions" ? (
                                 <ListBuilderActions
                                    handleLiestEditForm={handleShowEditForm}
                                    handleOpenParentFormEdit={handleOpenParentFormEdit}
                                    handleCopyList={handleCopyList}
                                    handleListDelete={handleListDelete}
                                    row={row}
                                    col={col}
                                    isEditLoading={loadingRowId === row.listId}
                                 />
                              ) : (
                                 (row as any)[col.key] || ""
                              )}
                           </td>
                        ))}
                     </tr>
                  ))}
            </tbody>
         </table>
         <Modal isOpen={isFormModalOpen} onCancel={handleCloseModal}>
            {formStatus === "error" ? (
               <div
                  style={{
                     display: "flex",
                     justifyContent: "center",
                     alignItems: "center",
                     height: "5rem",
                     fontSize: "x-large",
                  }}
               >
                  <IntlMessages id="Fetching.form.error" />
               </div>
            ) : (
               selectedFormId && (
                  //@ts-ignore
                  <FormV3Controller formId={selectedFormId} onStatusChange={setFormStatus} />
               )
            )}
         </Modal>
         <CreateOrEditListModal
            isOpen={isEditModalOpen}
            setIsOpen={setIsEditModalOpen}
            row={selectedRow}
         />
      </div>
   );
};

export default ListBuilderTable;
